import { useCallback, useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { SCREEN_PREFERENCE_CODES } from '@/routes';

import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { holidayService } from '@/api/domains/attendance';
import type { HolidayItem } from '@/api/domains/attendance';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { formatToDdMmYyyy } from '@/utils';

import { useHolidayExport } from './useHolidayExport';


type HolidayFilters = {
  year: number | null;
  month: number | null;
  holidayTypeId: number | null;
};

export interface UseHolidayListResult {
  rows: HolidayItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<HolidayItem>[];
  actions: TableAction<HolidayItem>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  refetch: () => void;
  openCreatePanel: () => void;
  onExport: () => Promise<void>;
  isExporting: boolean;
  filters: HolidayFilters;
  setFilters: (filters: HolidayFilters) => void;
  yearOptions: { value: string; label: string }[];
  monthOptions: { value: string; label: string }[];
}

export function useHolidayList(): UseHolidayListResult {
  const { showApiError, showSuccess, openModal, openSidePanel } = useUI();
  const { t: translate } = useTranslation();
  const { canEdit, canDelete, canCreate } = usePagePermissions();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<HolidayFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<HolidayItem> | null> => {
      const response = await holidayService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          sort: params.sort
            ? {
              key: params.sort.key,
              direction: params.sort.direction.toLowerCase() as 'asc' | 'desc',
            }
            : undefined,
          year: params.filters.year ?? undefined,
          month: params.filters.month ?? undefined,
          holidayTypeId: params.filters.holidayTypeId ?? undefined,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:holiday.messages.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    refetch,
    sort,
    debouncedSearchText,
    filters,
    setFilters,
  } = usePaginatedList<HolidayItem, HolidayFilters>({
    fetchFn,
    initialFilters: { year: null, month: null, holidayTypeId: null },
    screenCode: SCREEN_PREFERENCE_CODES.FESTIVOS_INHABILES_BY_TYPE,
  });

  const { onExport, isExporting } = useHolidayExport({
    searchText: debouncedSearchText,
    sort,
    year: filters.year,
    month: filters.month,
    holidayTypeId: filters.holidayTypeId,
  });

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);
    return [
      { value: '', label: translate('attendance:holiday.filters.allYears') },
      ...years.map((y) => ({ value: String(y), label: String(y) })),
    ];
  }, [translate]);

  const monthOptions = useMemo(() => [
    { value: '', label: translate('attendance:holiday.filters.allMonths') },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1),
      label: translate(`attendance:holiday.filters.months.${i + 1}`),
    })),
  ], [translate]);

  const handleEdit = useCallback(
    (row: HolidayItem) => {
      openSidePanel({
        title: translate('attendance:holiday.form.editTitle'),
        component: 'EditHolidayFormPanel',
        props: { holidayData: row, onSubmit: refetch },
      });
    },
    [openSidePanel, translate, refetch],
  );

  const handleDelete = useCallback((row: HolidayItem) => {
    openModal('ConfirmModal', {
      variant: 'error',
      icon: 'alert',
      title: translate('attendance:holiday.modal.deleteTitle'),
      bannerText: formatToDdMmYyyy(row.date),
      bodyText: translate('attendance:holiday.modal.deleteBody'),
      onConfirm: async () => {
        const response = await holidayService.delete(row.id);
        if (!response.success) {
          showApiError(response, translate('attendance:holiday.messages.deleteError'));
          return;
        }
        showSuccess(translate('attendance:holiday.messages.deleteSuccess'));
        refetch();
      },
    });
  }, [openModal, translate, showApiError, showSuccess, refetch]);

  const columns: TableColumn<HolidayItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('attendance:holiday.fields.id'),
        sortable: true,
      },
      {
        key: 'reason',
        header: translate('attendance:holiday.fields.reason'),
        sortable: true,
      },
      {
        key: 'type',
        header: translate('attendance:holiday.fields.type'),
        sortable: true,
      },
      {
        key: 'date',
        header: translate('attendance:holiday.fields.date'),
        sortable: true,
        render: (item) => formatToDdMmYyyy(item.date),
      },
      {
        key: 'nonWaivable',
        header: translate('attendance:holiday.fields.nonWaivable'),
        sortable: true,
        render: (item) =>
          item.nonWaivable
            ? translate('attendance:holiday.nonWaivable.yes')
            : translate('attendance:holiday.nonWaivable.no'),
      },
    ],
    [translate],
  );

  // Use `hidden` property instead of conditional array spread to preserve literal icon types.
  const actions: TableAction<HolidayItem>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:tableActions.edit'),
        icon: 'edit',
        hidden: () => !canEdit,
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: translate('common:tableActions.delete'),
        icon: 'close',
        hidden: () => !canDelete,
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [translate, canEdit, canDelete, handleEdit, handleDelete],
  );

  const openCreatePanel = useCallback(() => {
    if (!canCreate) return;
    openSidePanel({
      title: translate('attendance:holiday.form.title'),
      component: 'CreateHolidayFormPanel',
      props: { onSubmit: refetch },
    });
  }, [canCreate, openSidePanel, translate, refetch]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    refetch,
    openCreatePanel,
    onExport,
    isExporting,
    filters,
    setFilters,
    yearOptions,
    monthOptions,
  };
}

export default useHolidayList;
